import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/api';
import './propertyDetails.css';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const defaultUtilityRows = () => Array.from({ length: 12 }, (_, index) => ({
    month: index + 1,
    waterCost: '',
    gasCost: '',
    energyCost: ''
}));

const mapUtilityRows = (rows) => {
    const byMonth = new Map((rows || []).map((row) => [Number(row.month), row]));
    return Array.from({ length: 12 }, (_, index) => {
        const month = index + 1;
        const row = byMonth.get(month);
        return {
            month,
            waterCost: row && row.waterCost !== undefined ? String(row.waterCost) : '',
            gasCost: row && row.gasCost !== undefined ? String(row.gasCost) : '',
            energyCost: row && row.energyCost !== undefined ? String(row.energyCost) : ''
        };
    });
};

const PropertyUtilitiesPage = () => {
    const { propertyId } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [utilitiesYear, setUtilitiesYear] = useState(new Date().getFullYear());
    const [utilityRows, setUtilityRows] = useState(defaultUtilityRows());
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });

    const formatCurrency = (value) => `$${(Number(value) || 0).toFixed(2)}`;

    const loadProperty = async () => {
        const response = await api.get('/property');
        if (response && response.data) {
            const currentProperty = response.data.find((item) => item.id === parseInt(propertyId, 10));
            setProperty(currentProperty || null);
        }
    };

    const loadUtilityCharges = async () => {
        if (!propertyId) return;
        setLoading(true);
        setLoadError('');
        try {
            const response = await api.get(`/utilityCharge/property/${propertyId}?year=${utilitiesYear}`);
            if (response && response.data && Array.isArray(response.data.months)) {
                setUtilityRows(mapUtilityRows(response.data.months));
            } else {
                setUtilityRows(defaultUtilityRows());
            }
        } catch (error) {
            console.error('failed to load utility charges', error);
            setUtilityRows(defaultUtilityRows());
            setLoadError('Unable to load utility charges for this year.');
        } finally {
            setLoading(false);
        }
    };

    const onUtilityChange = (month, field, value) => {
        setUtilityRows((current) => current.map((row) => {
            if (row.month !== month) return row;
            return { ...row, [field]: value };
        }));
    };

    const onUtilitySave = async (event) => {
        event.preventDefault();
        try {
            const payload = {
                year: Number(utilitiesYear),
                months: utilityRows.map((row) => ({
                    month: row.month,
                    waterCost: parseFloat(row.waterCost) || 0,
                    gasCost: parseFloat(row.gasCost) || 0,
                    energyCost: parseFloat(row.energyCost) || 0
                }))
            };

            const response = await api.post(`/utilityCharge/property/${propertyId}`, payload);
            if (response && response.data && Array.isArray(response.data.months)) {
                setUtilityRows(mapUtilityRows(response.data.months));
                setMessage({ type: 'success', text: 'Monthly utility charges saved successfully.' });
            }
        } catch (error) {
            console.error('failed to save utility charges', error);
            setMessage({ type: 'error', text: 'Failed to save utility charges; please try again.' });
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, []);

    useEffect(() => {
        if (propertyId) {
            loadProperty();
            loadUtilityCharges();
        }
    }, [propertyId, utilitiesYear]);

    const totalWater = utilityRows.reduce((sum, row) => sum + (Number(row.waterCost) || 0), 0);
    const totalGas = utilityRows.reduce((sum, row) => sum + (Number(row.gasCost) || 0), 0);
    const totalEnergy = utilityRows.reduce((sum, row) => sum + (Number(row.energyCost) || 0), 0);
    const totalUtilities = totalWater + totalGas + totalEnergy;

    return (
        <div className="property-pnl-page property-utilities-page">
            <div className="property-pnl-toolbar">
                <button className="btn-secondary" onClick={() => navigate(`/PropertyDetails/${propertyId}`)}>
                    ← Back to Property Details
                </button>
                <div className="property-pnl-title-block">
                    <h2>{property?.name || 'Property'} Utility Charges</h2>
                    <p>{property ? `${property.address1}, ${property.city}, ${property.state}` : 'Loading property...'}</p>
                </div>
                <div className="pnl-controls">
                    <label htmlFor="utilitiesYearPage">Year</label>
                    <input
                        id="utilitiesYearPage"
                        type="number"
                        value={utilitiesYear}
                        onChange={(event) => setUtilitiesYear(event.target.value)}
                    />
                    <button type="button" className="btn-primary" onClick={loadUtilityCharges}>Reload</button>
                </div>
            </div>

            {loading && (
                <div className="app-state-card app-state-info" role="status" aria-live="polite">
                    <p>Loading utility charges...</p>
                </div>
            )}

            {!loading && loadError && (
                <div className="app-state-card app-state-error" role="alert">
                    <p>{loadError}</p>
                    <div>
                        <button type="button" className="btn-primary" onClick={loadUtilityCharges}>Retry</button>
                    </div>
                </div>
            )}

            {message.text && (
                <div className={`app-notice ${message.type === 'success' ? 'app-notice-success' : 'app-notice-error'}`}>
                    {message.text}
                </div>
            )}

            <div className="investment-summary-grid utility-summary-grid">
                <div className="property-pnl-card investment-summary-card">
                    <span className="investment-summary-label">Water Total</span>
                    <strong>{formatCurrency(totalWater)}</strong>
                </div>
                <div className="property-pnl-card investment-summary-card">
                    <span className="investment-summary-label">Gas Total</span>
                    <strong>{formatCurrency(totalGas)}</strong>
                </div>
                <div className="property-pnl-card investment-summary-card">
                    <span className="investment-summary-label">Energy Total</span>
                    <strong>{formatCurrency(totalEnergy)}</strong>
                </div>
                <div className="property-pnl-card investment-summary-card">
                    <span className="investment-summary-label">Utility Total</span>
                    <strong>{formatCurrency(totalUtilities)}</strong>
                </div>
            </div>

            <div className="property-pnl-card">
                <form onSubmit={onUtilitySave} className="utility-page-form">
                    {loading || loadError ? (
                        <></>
                    ) : (
                        <div className="property-pnl-table-wrap">
                            <table className="utilities-table property-utility-table">
                                <thead>
                                    <tr>
                                        <th>Month</th>
                                        <th>Water</th>
                                        <th>Gas</th>
                                        <th>Energy</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {utilityRows.map((row) => {
                                        const rowTotal = (Number(row.waterCost) || 0) + (Number(row.gasCost) || 0) + (Number(row.energyCost) || 0);
                                        return (
                                            <tr key={row.month}>
                                                <td>{MONTH_LABELS[row.month - 1]}</td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={row.waterCost}
                                                        onChange={(event) => onUtilityChange(row.month, 'waterCost', event.target.value)}
                                                        placeholder="0.00"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={row.gasCost}
                                                        onChange={(event) => onUtilityChange(row.month, 'gasCost', event.target.value)}
                                                        placeholder="0.00"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={row.energyCost}
                                                        onChange={(event) => onUtilityChange(row.month, 'energyCost', event.target.value)}
                                                        placeholder="0.00"
                                                    />
                                                </td>
                                                <td>{formatCurrency(rowTotal)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="modal-buttons utility-page-actions">
                        <button type="submit" className="btn-primary" disabled={loading}>Save Utility Charges</button>
                        <button type="button" className="btn-secondary" onClick={() => navigate(`/PropertyDetails/${propertyId}`)}>Back to Property</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PropertyUtilitiesPage;